import _ from "lodash";

import axios from "axios";
import apiUrl, { customerApiUrl } from "api-url";
import { getActiveOrganization } from "modules/organizations/OrganizationsState";
import {
  isCarrier,
  isPartner,
  isShipper,
} from "shared/utils/organizations.utils";

const STORE_MOUNT_POINT = "inventoryViewDashboard";

//urls
const LOCATION_VISIBILITY_BASE_URL = apiUrl(
  "/entity-inventory/location-visibility",
);

// Actions
const REQUEST_WATCHED_LOCATIONS = `${STORE_MOUNT_POINT}/REQUEST_WATCHED_LOCATIONS`;
const REQUEST_ALL_WATCHED_LOCATIONS = `${STORE_MOUNT_POINT}/REQUEST_ALL_WATCHED_LOCATIONS`;
const FETCH_WATCHED_LOCATIONS = `${STORE_MOUNT_POINT}/FETCH_WATCHED_LOCATIONS`;
const FETCH_ALL_WATCHED_LOCATIONS = `${STORE_MOUNT_POINT}/FETCH_ALL_WATCHED_LOCATIONS`;
const REQUEST_WATCHED_LOCATIONS_COUNT = `${STORE_MOUNT_POINT}/REQUEST_WATCHED_LOCATIONS_COUNT`;
const RECEIVE_WATCHED_LOCATIONS_COUNT = `${STORE_MOUNT_POINT}/RECEIVE_WATCHED_LOCATIONS_COUNT`;
const RECEIVE_WATCHED_LOCATIONS_ERROR = `${STORE_MOUNT_POINT}/RECEIVE_WATCHED_LOCATIONS_ERROR`;
const SET_WATCHED_LOCATIONS_PAGE_INDEX = `${STORE_MOUNT_POINT}/SET_WATCHED_LOCATIONS_PAGE_INDEX`;
const FETCH_INITIAL_ETA_LOCATIONS = `${STORE_MOUNT_POINT}/FETCH_INITIAL_ETA_LOCATIONS`;
const RECEIVE_INITIAL_ETA_LOCATIONS = `${STORE_MOUNT_POINT}/RECEIVE_INITIAL_ETA_LOCATIONS`;
const ERROR_INITIAL_ETA_LOCATIONS = `${STORE_MOUNT_POINT}/ERROR_INITIAL_ETA_LOCATIONS`;

const FETCH_GATE_RELEASE_THRESHOLD_LOCATIONS = `${STORE_MOUNT_POINT}/FETCH_GATE_RELEASE_THRESHOLD_LOCATIONS`;
const RECEIVE_GATE_RELEASE_THRESHOLD_LOCATIONS = `${STORE_MOUNT_POINT}/RECEIVE_GATE_RELEASE_THRESHOLD_LOCATIONS`;
const ERROR_GATE_RELEASE_THRESHOLD_LOCATIONS = `${STORE_MOUNT_POINT}/ERROR_GATE_RELEASE_THRESHOLD_LOCATIONS`;

const PAGE_SIZE = 12;

function fetchWatchedLocations(pageNumber) {
  return async (dispatch, getState) => {
    dispatch({
      type: REQUEST_WATCHED_LOCATIONS,
    });
    dispatch({ type: REQUEST_WATCHED_LOCATIONS_COUNT });
    const state = getState();

    const fvId = getActiveOrganization(state)?.fv_id;
    const params = { pageSize: PAGE_SIZE, pageNumber };
    const url = `${LOCATION_VISIBILITY_BASE_URL}/organization/${fvId}?watched=true`;

    await axios
      .get(url, { params })
      .then((response) => {
        dispatch({
          type: RECEIVE_WATCHED_LOCATIONS_COUNT,
          payload: { totalPages: response.data?.meta?.totalPages ?? 1 },
        });
        return dispatch({
          type: FETCH_WATCHED_LOCATIONS,
          payload: { watchedLocations: response?.data?.data ?? [] },
        });
      })
      .catch((err) => {
        dispatch({ type: RECEIVE_WATCHED_LOCATIONS_ERROR, err });
      });
  };
}

const fetchAllWatchedLocations = () => {
  return async (dispatch, getState) => {
    dispatch({
      type: REQUEST_ALL_WATCHED_LOCATIONS,
    });
    const state = getState();
    const fvId = getActiveOrganization(state)?.fv_id;
    const url = `${LOCATION_VISIBILITY_BASE_URL}/organization/${fvId}?watched=true`;

    await axios
      .get(url)
      .then((response) => {
        return dispatch({
          type: FETCH_ALL_WATCHED_LOCATIONS,
          payload: { allWatchedLocations: response?.data?.data ?? [] },
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };
};

const saveWatchedLocations = (newWatchedLocations, deletedWatchedLocations) => {
  let promises = [];

  if (!_.isEmpty(newWatchedLocations)) {
    promises.push(axios.post(apiUrl("/location/watch"), newWatchedLocations));
  }

  if (!_.isEmpty(deletedWatchedLocations)) {
    promises.push(
      axios.post(apiUrl("/location/unwatch"), deletedWatchedLocations),
    );
  }

  return (_) => {
    return Promise.all(promises);
  };
};

function setWatchedLocationsPageIndex(pageIndex) {
  return {
    type: SET_WATCHED_LOCATIONS_PAGE_INDEX,
    payload: { pageIndex },
  };
}

function redirectToVinDetails(vin) {
  return (dispatch, getState) => {
    const organization = getActiveOrganization(getState());
    const isShipperOrg = isShipper(organization);
    const isCarrierOrg = isCarrier(organization);
    const isPartnerOrg = isPartner(organization);
    if (isShipperOrg) {
      dispatch({
        type: "VIN_DETAILS",
        payload: { entity_id: vin.id },
      });
    } else if (isCarrierOrg) {
      dispatch({
        type: "CARRIERVIEW_DETAILS",
        payload: { internal_id: vin.internal_id },
      });
    } else if (isPartnerOrg) {
      dispatch({
        type: "PARTNERVIEW_DETAILS",
        payload: { internal_id: vin.internal_id },
      });
    } else {
      // Ignored for other org types.
    }
  };
}

const transformLocationData = (inventoryViewLocations) => {
  if (inventoryViewLocations.length > 0) {
    return inventoryViewLocations.map((item) => {
      const location = item.location;
      return {
        id: location.id,
        name: location.name,
        code: location.code,
        vinCount: item.count,
      };
    });
  }
  return [];
};

const fetchInitialETALocations = (searchOption) => {
  return async (dispatch, getState) => {
    dispatch({
      type: FETCH_INITIAL_ETA_LOCATIONS,
    });
    const url = customerApiUrl(
      `/entity-inventory/organization/metrics/initial-eta/locations?initialEta=${searchOption}`,
    );

    await axios
      .get(url)
      .then((response) => {
        const locations = response?.data?.locationAggregation ?? [];
        return dispatch({
          type: RECEIVE_INITIAL_ETA_LOCATIONS,
          payload: {
            initialETALocations: locations
              ? transformLocationData(locations)
              : [],
          },
        });
      })
      .catch((err) => {
        // console.log(err);
        dispatch({
          type: ERROR_INITIAL_ETA_LOCATIONS,
        });
      });
  };
};

const fetchGateReleaseThresholdLocations = (searchOption) => {
  return async (dispatch, getState) => {
    dispatch({
      type: FETCH_GATE_RELEASE_THRESHOLD_LOCATIONS,
    });
    const url = customerApiUrl(
      `/entity-inventory/organization/metrics/gate-release-threshold/locations?gateReleaseThreshold=${searchOption}`,
    );

    await axios
      .get(url)
      .then((response) => {
        const locations = response?.data?.locationAggregation ?? [];
        return dispatch({
          type: RECEIVE_GATE_RELEASE_THRESHOLD_LOCATIONS,
          payload: {
            gateReleaseThresholdLocations: locations
              ? transformLocationData(locations)
              : [],
          },
        });
      })
      .catch((err) => {
        // console.log(err);
        dispatch({
          type: ERROR_GATE_RELEASE_THRESHOLD_LOCATIONS,
        });
      });
  };
};

// Selectors
const getWatchedLocations = (state) =>
  state[STORE_MOUNT_POINT].watchedLocations ?? [];
const getAllWatchedLocations = (state) =>
  state[STORE_MOUNT_POINT].allWatchedLocations ?? [];

const getWatchedLocationsLoading = (state) =>
  state[STORE_MOUNT_POINT].isWatchLocationsLoading;

const getAllWatchedLocationsLoading = (state) =>
  state[STORE_MOUNT_POINT].isAllWatchLocationsLoading;

const getWatchedLocationsError = (state) =>
  state[STORE_MOUNT_POINT].watchedLocationsError;

const getWatchedLocationsPageIndex = (state) =>
  state[STORE_MOUNT_POINT].watchedLocationsPageIndex;
const getWatchedLocationsPageSize = (state) =>
  state[STORE_MOUNT_POINT].watchedLocationsPageSize;
const getWatchedLocationsPageCount = (state) =>
  state[STORE_MOUNT_POINT].watchedLocationsPageCount;
const getDwellingVinsWidgetPageIndex = (state) =>
  state[STORE_MOUNT_POINT].dwellingVinsWidgetPageIndex;

const getInitialETALocations = (state) =>
  state[STORE_MOUNT_POINT].initialETALocations ?? [];

const getInitialETALocationsLoading = (state) =>
  state[STORE_MOUNT_POINT].isInitialETALocationsLoading;

const getInitialETALocationsError = (state) =>
  state[STORE_MOUNT_POINT].isInitialETALocationsError;

const getGateReleaseThresholdLocations = (state) =>
  state[STORE_MOUNT_POINT].gateReleaseThresholdLocations ?? [];

const getGateReleaseThresholdLocationsLoading = (state) =>
  state[STORE_MOUNT_POINT].isGateReleaseThresholdLocationsLoading;

const getGateReleaseThresholdLocationsError = (state) =>
  state[STORE_MOUNT_POINT].isGateReleaseThresholdLocationsError;

// Reducers
const inventoryViewDashboardReducer = (
  state = {
    watchedLocations: [],
    allWatchedLocations: [],
    isWatchLocationsLoading: false,
    isAllWatchLocationsLoading: false,
    watchedLocationsPageIndex: 0,
    watchedLocationsPageCount: 0,
    watchedLocationsError: {},
    isInitialETALocationsLoading: false,
    isInitialETALocationsError: false,
    initialETALocations: [],
    isGateReleaseThresholdLocationsLoading: false,
    isGateReleaseThresholdLocationsError: false,
    gateReleaseThresholdLocations: [],
  },
  action,
) => {
  switch (action.type) {
    case REQUEST_WATCHED_LOCATIONS:
      return {
        ...state,
        isWatchLocationsLoading: true,
        watchedLocationsError: {},
      };
    case REQUEST_ALL_WATCHED_LOCATIONS:
      return {
        ...state,
        isAllWatchLocationsLoading: true,
      };
    case FETCH_WATCHED_LOCATIONS:
      return {
        ...state,
        isWatchLocationsLoading: false,
        watchedLocations: action.payload.watchedLocations,
      };
    case FETCH_ALL_WATCHED_LOCATIONS:
      return {
        ...state,
        isAllWatchLocationsLoading: false,
        allWatchedLocations: action.payload.allWatchedLocations,
      };
    case RECEIVE_WATCHED_LOCATIONS_ERROR:
      return {
        ...state,
        isWatchLocationsLoading: false,
        watchedLocationsError: action.err,
      };
    case RECEIVE_WATCHED_LOCATIONS_COUNT:
      return {
        ...state,
        watchedLocationsPageCount: action.payload.totalPages,
      };

    case SET_WATCHED_LOCATIONS_PAGE_INDEX:
      return {
        ...state,
        watchedLocationsPageIndex: action.payload.pageIndex,
      };
    case FETCH_INITIAL_ETA_LOCATIONS:
      return {
        ...state,
        isInitialETALocationsLoading: true,
        isInitialETALocationsError: false,
      };
    case RECEIVE_INITIAL_ETA_LOCATIONS:
      return {
        ...state,
        isInitialETALocationsLoading: false,
        isInitialETALocationsError: false,
        initialETALocations: action.payload.initialETALocations,
      };
    case ERROR_INITIAL_ETA_LOCATIONS:
      return {
        ...state,
        isInitialETALocationsError: true,
      };
    case FETCH_GATE_RELEASE_THRESHOLD_LOCATIONS:
      return {
        ...state,
        isGateReleaseThresholdLocationsLoading: true,
        isGateReleaseThresholdLocationsError: false,
      };
    case RECEIVE_GATE_RELEASE_THRESHOLD_LOCATIONS:
      return {
        ...state,
        isGateReleaseThresholdLocationsLoading: false,
        isGateReleaseThresholdLocationsError: false,
        gateReleaseThresholdLocations:
          action.payload.gateReleaseThresholdLocations,
      };
    case ERROR_GATE_RELEASE_THRESHOLD_LOCATIONS:
      return {
        ...state,
        isGateReleaseThresholdLocationsError: true,
      };
    default:
      return state;
  }
};

const InventoryViewDashboardState = {
  mountPoint: STORE_MOUNT_POINT,
  actionTypes: {},
  actionCreators: {
    fetchWatchedLocations,
    fetchAllWatchedLocations,
    saveWatchedLocations,
    setWatchedLocationsPageIndex,
    redirectToVinDetails,
    fetchInitialETALocations,
    fetchGateReleaseThresholdLocations,
  },
  selectors: {
    getWatchedLocations,
    getAllWatchedLocations,
    getWatchedLocationsError,
    getWatchedLocationsLoading,
    getAllWatchedLocationsLoading,
    getWatchedLocationsPageIndex,
    getWatchedLocationsPageSize,
    getWatchedLocationsPageCount,
    getDwellingVinsWidgetPageIndex,
    getInitialETALocations,
    getInitialETALocationsLoading,
    getInitialETALocationsError,
    getGateReleaseThresholdLocations,
    getGateReleaseThresholdLocationsLoading,
    getGateReleaseThresholdLocationsError,
  },
  reducer: inventoryViewDashboardReducer,
};
export default InventoryViewDashboardState;
